import type { DatePickerModalProps } from '../modals/DatePickerModal'
import type { EventInfoProps } from '../modals/EventInfo'

// Action Types
const SHOW_MODAL = 'SHOW_MODAL'
const HIDE_MODAL = 'HIDE_MODAL'

type ModalPropsByComponent = {
  DatePickerModal: DatePickerModalProps
  EventInfo: EventInfoProps
}

type ModalComponentName = keyof ModalPropsByComponent
type ModalProps = ModalPropsByComponent[ModalComponentName]

type ShowModalActionByName<K extends ModalComponentName> = {
  type: typeof SHOW_MODAL
  componentName: K
  properties: ModalPropsByComponent[K]
}

type ShowModalAction = {
  [K in ModalComponentName]: ShowModalActionByName<K>
}[ModalComponentName]

type HideModalAction = {
  type: typeof HIDE_MODAL
}

type ModalAction = ShowModalAction | HideModalAction

const MODAL_ORIGINAL_PADDING_RIGHT_DATASET_KEY = 'modalOriginalPaddingRight'

function lockBodyScroll() {
  const body = document.body
  if (!body || body.classList.contains('no-scroll')) return

  const scrollbarWidth =
    window.innerWidth - document.documentElement.clientWidth
  body.dataset[MODAL_ORIGINAL_PADDING_RIGHT_DATASET_KEY] =
    body.style.paddingRight

  if (scrollbarWidth > 0) {
    const currentPaddingRight =
      Number.parseFloat(window.getComputedStyle(body).paddingRight) || 0
    body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`
  }

  body.classList.add('no-scroll')
}

function unlockBodyScroll() {
  const body = document.body
  if (!body) return

  body.classList.remove('no-scroll')

  const originalPaddingRight =
    body.dataset[MODAL_ORIGINAL_PADDING_RIGHT_DATASET_KEY]
  if (typeof originalPaddingRight !== 'undefined') {
    body.style.paddingRight = originalPaddingRight
    delete body.dataset[MODAL_ORIGINAL_PADDING_RIGHT_DATASET_KEY]
  } else {
    body.style.removeProperty('padding-right')
  }
}

// Action Creators
export const showModal = <K extends ModalComponentName>(
  componentName: K,
  properties: ModalPropsByComponent[K],
): ShowModalActionByName<K> => {
  lockBodyScroll()
  return {
    type: SHOW_MODAL,
    componentName,
    properties,
  }
}

export const hideModal = (): HideModalAction => {
  unlockBodyScroll()
  return {
    type: HIDE_MODAL,
  }
}

// Reducer
export interface ModalState {
  visible: boolean
  componentName?: ModalComponentName
  properties?: ModalProps
}

const initialState: ModalState = {
  visible: false,
}

export default function modalReducer(
  state: ModalState = initialState,
  action: ModalAction,
): ModalState {
  switch (action.type) {
    case SHOW_MODAL:
      return Object.assign({}, state, {
        componentName: action.componentName,
        properties: action.properties,
        visible: true,
      })
    case HIDE_MODAL:
      return Object.assign({}, state, {
        visible: false,
      })
    default:
      return state
  }
}
